import { clsx } from '@alexis/helpers/clsx';
import { translateText } from '@alexis/helpers/translation';
import NewFlightTimesFilter from '@pages/FlightSearchResult/components/FlightFilters/NewFlightTimesFilter';
import { FlightFilterCategories } from '@pages/FlightSearchResult/components/FlightFilters/type';
import { useFlightPLS } from '@pages/FlightSearchResult/contexts/FlightPerLegSearchContext/FlightPerLegSearchContext';
import { useFlightSearchResults } from '@pages/FlightSearchResult/contexts/FlightSearchResultsContext';
import useFlightSearchFilters from '@pages/FlightSearchResult/hooks/useFlightSearchFilters';
import useFlightSearchPageParams from '@pages/FlightSearchResult/hooks/useFlightSearchPageParams';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import {
  PAYMENT_METHOD_FILTER_BASED_ON_POS,
  FLIGHT_SEARCH_RESULTS_HEADER_HEIGHT,
  TEST_FEATURES_PARAM,
  MOCK_BAGGAGE_FILTER,
} from '@constants/flight';

import { useFlightContext } from '@context/FlightContext';

import CloseIcon from '@icons/close.svg';

import { useBodyScrollable } from '@hooks/useBodyScrollable';

import {
  getIsRtlState,
  getLocaleState,
  getTranslationsState,
  getCurrentSiteState,
} from '@redux/selectors';

import AirlinesFilter from './AirlinesFilter';
import BookingOptionsFilter from './BookingOptionsFilter';
import BookingSitesFilter from './BookingSitesFilter';
import DurationFilter from './DurationFilter';
import FilterCategoryAccordion from './FilterCategoryAccordion';
import FlightExperiencesFilter from './FlightExperiencesFilter';
import styles from './FlightFilters.module.scss';
import FlightFiltersSkeleton from './FlightFiltersSkeleton';
import FlightTimesFilter from './FlightTimesFilter';
import MultiCityLegSelector from './MultiCityLegSelector';
import MultiCityPriceFilter from './MultiCityPriceFilter';
import OriginDestinationFilter from './OriginDestinationFilter';
import PaymentMethodsFilter from './PaymentMethodsFilter';
import PriceFilter from './PriceFilter';
import StopoverAirportsFilter from './StopoverAirportsFilter';
import StopsFilter from './StopsFilter';

interface FlightFiltersProps {
  className?: string;
  isAllFiltersOpen?: boolean;
  onAllFiltersClose?: () => void;
  availablePaymentMethods: HomepageFlightsPaymentMethod[];
  onReset(): void;
  genzoTrackApplyFilterActionEvent: (
    eventDataObjectType: string,
    eventDataActionType: string,
    eventDataPair: {
      [key: string]: any;
    },
  ) => void;
  genzoTrackApplyFilterSearchView: () => void;
}

const FlightFilters: React.FC<FlightFiltersProps> = ({
  isAllFiltersOpen,
  className,
  availablePaymentMethods,
  onReset,
  onAllFiltersClose,
  genzoTrackApplyFilterActionEvent,
  genzoTrackApplyFilterSearchView,
}) => {
  const {
    tripViewModels,
    sponsoredAdTripViewModels,
    sortedAndFilteredTripViewModels,
    filteredSponsoredAdTripViewModels,
    providerFilterOptions,
  } = useFlightSearchResults();
  const { isPerLegSearch } = useFlightPLS();

  const { selectedLegs, appliedFilters } = useFlightSearchFilters();

  const disable = selectedLegs.length > 0;
  const tripsCount = tripViewModels.length + sponsoredAdTripViewModels.length;
  const filteredTripsCount =
    sortedAndFilteredTripViewModels.filter((item) => !item.isPlaceholder && !item.sponsored)
      .length + filteredSponsoredAdTripViewModels.length;

  const isRtl = useSelector(getIsRtlState);
  const translations = useSelector(getTranslationsState);
  const locale = useSelector(getLocaleState);

  const { isMultiCity, paymentMethods: paymentMethodsFromParams } = useFlightSearchPageParams();

  const [, setIsBodyScrollable] = useBodyScrollable();

  const { flightSearchFormHeight, isFlightSearch2AndNotMulticity } = useFlightContext();

  const filterContainerRef = useRef<HTMLDivElement>(null);

  const [isFiltersDrawerOpen, setIsFiltersDrawerOpen] = useState(false);

  const [isPositionSticky, setIsPositionSticky] = useState(false);

  const currentSite = useSelector(getCurrentSiteState);

  const { isApplyBaggageFilter, setIsApplyBaggageFilter } = useFlightContext();

  const [searchParams] = useSearchParams();

  const isShowBaggageFilter = useMemo(() => {
    const testFeaturesSearchParam = searchParams.get(TEST_FEATURES_PARAM);

    return (
      testFeaturesSearchParam && testFeaturesSearchParam.split(',').includes(MOCK_BAGGAGE_FILTER)
    );
  }, [searchParams]);

  const handleBaggageFilterClick = () => {
    setIsApplyBaggageFilter((prev) => !prev);
  };

  const paymentMethods = availablePaymentMethods.filter(
    (paymentMethod) =>
      paymentMethodsFromParams.includes(paymentMethod.id) &&
      (
        PAYMENT_METHOD_FILTER_BASED_ON_POS[currentSite.countryCode] ||
        PAYMENT_METHOD_FILTER_BASED_ON_POS.default
      ).includes(paymentMethod.name.toLowerCase()),
  );

  const hasWegoFareProvider = providerFilterOptions.some((provider) => provider.wegoFare);

  const hasDirectAirlineProvider = providerFilterOptions.some(
    (provider) => provider.type === 'airline',
  );

  const handleResetFilter = () => {
    onReset();
    setIsFiltersDrawerOpen(false);
  };

  useEffect(() => {
    setIsPositionSticky(isAllFiltersOpen || false);
  }, [isAllFiltersOpen]);

  // Update the position of the container based on window scroll
  useEffect(() => {
    let lastScrollTop = window.scrollY;
    let positionSticky = false;

    const filterBar = filterContainerRef.current;

    const setScrollPosition = () => {
      const isSearchFormSticky = window.scrollY > FLIGHT_SEARCH_RESULTS_HEADER_HEIGHT;

      const windowHeight = window.innerHeight;
      const filterBarHeight = filterBar!.offsetHeight;

      const scrollTop = window.scrollY;
      const scrollBottom = scrollTop + windowHeight;

      const sidebarTop = filterBar!.offsetTop;
      const sidebarBottom = sidebarTop + filterBarHeight;

      const heightDelta = Math.abs(windowHeight - filterBarHeight);
      const scrollDelta = lastScrollTop - scrollTop;

      const isScrollingDown = scrollTop > lastScrollTop;
      const isWindowLarger =
        windowHeight > filterBarHeight + (isSearchFormSticky ? flightSearchFormHeight : 0);
      const initialFilterBarTop = isSearchFormSticky
        ? flightSearchFormHeight
        : filterBar!.offsetTop;

      if (
        (isWindowLarger && scrollTop > initialFilterBarTop) ||
        (!isWindowLarger && scrollTop > initialFilterBarTop + heightDelta)
      ) {
        setIsPositionSticky(true);
        positionSticky = true;
      } else if (!isScrollingDown && scrollTop <= initialFilterBarTop) {
        setIsPositionSticky(false);
        positionSticky = false;
      }

      const dragBottomDown = sidebarBottom <= scrollBottom && isScrollingDown;
      const dragTopUp = sidebarTop >= scrollTop && !isScrollingDown;

      if (dragBottomDown) {
        if (isWindowLarger) {
          filterBar!.style.top = isSearchFormSticky ? `${flightSearchFormHeight}px` : '0';
        } else {
          filterBar!.style.top = -heightDelta + 'px';
        }
      } else if (dragTopUp) {
        filterBar!.style.top = isSearchFormSticky ? `${flightSearchFormHeight}px` : '0';
      } else if (positionSticky) {
        const currentTop = parseInt(filterBar!.style.top, 10);

        const minTop = -heightDelta;
        const scrolledTop = currentTop + scrollDelta;

        const isPageAtBottom = scrollTop + windowHeight >= document.documentElement.scrollHeight;
        let newTop = isPageAtBottom ? minTop : scrolledTop;

        if (isSearchFormSticky) {
          newTop = newTop > flightSearchFormHeight ? flightSearchFormHeight : newTop;
        } else {
          newTop = newTop > 0 ? 0 : newTop;
        }

        filterBar!.style.top = newTop + 'px';
      }

      lastScrollTop = scrollTop;
    };

    setScrollPosition();

    if (filterBar !== null) {
      window.addEventListener('scroll', setScrollPosition);
      return () => window.removeEventListener('scroll', setScrollPosition);
    }
  }, [flightSearchFormHeight]);

  // Disable body scroll if filters drawer is open
  useEffect(() => {
    if (isFiltersDrawerOpen) {
      setIsBodyScrollable(false);
    } else {
      setIsBodyScrollable(true);
    }
  }, [isFiltersDrawerOpen, setIsBodyScrollable]);

  useEffect(() => {
    if (isAllFiltersOpen) {
      const handleBodyClick = () => {
        onAllFiltersClose?.();
      };

      const timerId = setTimeout(() => {
        document.body.addEventListener('click', handleBodyClick);
      }, 0);

      return () => {
        clearTimeout(timerId);
        document.body.removeEventListener('click', handleBodyClick);
      };
    }
  }, [isAllFiltersOpen]);

  return (
    <>
      <div
        className={clsx(
          styles.filtersButtonWrapper,
          isFlightSearch2AndNotMulticity && styles.hideDefaultFilter,
        )}
      >
        <button
          type='button'
          className={styles.filtersButton}
          onClick={() => setIsFiltersDrawerOpen(true)}
        >
          <i className={clsx(styles.chevron, isRtl ? styles.right : styles.left)}></i>
          {translations.filters}
        </button>
      </div>

      <div
        className={clsx(
          styles.filterOverlay,
          (isAllFiltersOpen || isFiltersDrawerOpen) && styles.open,
          isFlightSearch2AndNotMulticity && styles.allFiltersfilterOverlay,
        )}
        onClick={() => setIsFiltersDrawerOpen(false)}
      ></div>

      <div
        className={clsx(
          styles.filterContainer,
          (isAllFiltersOpen || isFiltersDrawerOpen) && styles.open,
          isFlightSearch2AndNotMulticity && styles.allFiltersFilterContainer,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.filterDrawerHeader}>
          {translations.filters}

          <CloseIcon
            className={styles.icon}
            onClick={() => {
              if (onAllFiltersClose) {
                onAllFiltersClose();
              }
              setIsFiltersDrawerOpen(false);
            }}
          />
        </div>

        <div
          ref={filterContainerRef}
          className={clsx(
            styles.mainFilter,
            isRtl && styles.rtl,
            className,
            disable && styles.disabled,
            isPositionSticky && styles.sticky,
          )}
        >
          <div className={styles.header}>
            {!tripsCount ? (
              <div className={styles.skeleton} />
            ) : (
              <>
                <div className={styles.resultsCount}>
                  {translateText(
                    translations.number_of_results,
                    locale,
                    filteredTripsCount,
                    tripsCount,
                  )}
                </div>
                <div className={styles.reset} onClick={handleResetFilter}>
                  {translations.clear}
                </div>
              </>
            )}
          </div>

          {isShowBaggageFilter && !isMultiCity && !isPerLegSearch && (
            <button
              className={clsx(styles.baggageFilterButton, isApplyBaggageFilter && styles.isActive)}
              onClick={handleBaggageFilterClick}
            >
              {/* This is only for internal testing purpose and no translations are needed */}
              {isApplyBaggageFilter ? 'Baggage Filter Applied' : 'Baggage Filter'}
            </button>
          )}

          <div className={styles.content}>
            {tripsCount ? (
              <>
                {isMultiCity && (
                  <MultiCityPriceFilter
                    genzoTrackApplyFilterActionEvent={genzoTrackApplyFilterActionEvent}
                    genzoTrackApplyFilterSearchView={genzoTrackApplyFilterSearchView}
                  />
                )}

                <div className={isMultiCity ? styles.multiCityWrapper : ''}>
                  {isMultiCity && <MultiCityLegSelector />}

                  {!isMultiCity && !!paymentMethods.length && (
                    <FilterCategoryAccordion
                      title={translations.payment_type as string}
                      isExpanded
                      isFiltered={appliedFilters.includes(FlightFilterCategories.PaymentMethod)}
                    >
                      <PaymentMethodsFilter
                        genzoTrackApplyFilterActionEvent={genzoTrackApplyFilterActionEvent}
                        availablePaymentMethods={availablePaymentMethods}
                      />
                    </FilterCategoryAccordion>
                  )}

                  <FilterCategoryAccordion
                    title={translations.stops as string}
                    isExpanded
                    isFiltered={appliedFilters.includes(FlightFilterCategories.Stops)}
                  >
                    <StopsFilter
                      genzoTrackApplyFilterActionEvent={genzoTrackApplyFilterActionEvent}
                      genzoTrackApplyFilterSearchView={genzoTrackApplyFilterSearchView}
                    />
                  </FilterCategoryAccordion>

                  {!isMultiCity && (hasWegoFareProvider || hasDirectAirlineProvider) && (
                    <FilterCategoryAccordion
                      title={translations.booking_options as string}
                      isExpanded
                      isFiltered={appliedFilters.includes(FlightFilterCategories.BookingOptions)}
                    >
                      <BookingOptionsFilter
                        genzoTrackApplyFilterActionEvent={genzoTrackApplyFilterActionEvent}
                        genzoTrackApplyFilterSearchView={genzoTrackApplyFilterSearchView}
                      />
                    </FilterCategoryAccordion>
                  )}

                  {!isMultiCity && (
                    <FilterCategoryAccordion
                      title={translations.price as string}
                      isExpanded
                      isFiltered={appliedFilters.includes(FlightFilterCategories.Price)}
                    >
                      <PriceFilter
                        genzoTrackApplyFilterActionEvent={genzoTrackApplyFilterActionEvent}
                        genzoTrackApplyFilterSearchView={genzoTrackApplyFilterSearchView}
                      />
                    </FilterCategoryAccordion>
                  )}

                  <FilterCategoryAccordion
                    title={translations.flight_experience as string}
                    isExpanded
                    isFiltered={appliedFilters.includes(FlightFilterCategories.Experience)}
                  >
                    <FlightExperiencesFilter
                      genzoTrackApplyFilterActionEvent={genzoTrackApplyFilterActionEvent}
                      genzoTrackApplyFilterSearchView={genzoTrackApplyFilterSearchView}
                    />
                  </FilterCategoryAccordion>

                  <FilterCategoryAccordion
                    title={translations.flight_times as string}
                    isExpanded
                    isFiltered={appliedFilters.includes(FlightFilterCategories.Times)}
                  >
                    {isPerLegSearch ? (
                      <NewFlightTimesFilter
                        genzoTrackApplyFilterActionEvent={genzoTrackApplyFilterActionEvent}
                      />
                    ) : (
                      <FlightTimesFilter
                        genzoTrackApplyFilterActionEvent={genzoTrackApplyFilterActionEvent}
                        genzoTrackApplyFilterSearchView={genzoTrackApplyFilterSearchView}
                      />
                    )}
                  </FilterCategoryAccordion>

                  <FilterCategoryAccordion
                    title={translations.duration as string}
                    isFiltered={appliedFilters.includes(FlightFilterCategories.Duration)}
                  >
                    <DurationFilter
                      genzoTrackApplyFilterActionEvent={genzoTrackApplyFilterActionEvent}
                      genzoTrackApplyFilterSearchView={genzoTrackApplyFilterSearchView}
                    />
                  </FilterCategoryAccordion>

                  <FilterCategoryAccordion
                    title={(isMultiCity ? translations.airlines : translations.airlines1) as string}
                    isExpanded
                    isFiltered={appliedFilters.includes(FlightFilterCategories.Airlines)}
                  >
                    <AirlinesFilter
                      genzoTrackApplyFilterActionEvent={genzoTrackApplyFilterActionEvent}
                      genzoTrackApplyFilterSearchView={genzoTrackApplyFilterSearchView}
                    />
                  </FilterCategoryAccordion>

                  {!isMultiCity && (
                    <FilterCategoryAccordion
                      title={translations.flight_origin as string}
                      isFiltered={appliedFilters.includes(
                        FlightFilterCategories.OriginAndDestination,
                      )}
                    >
                      <OriginDestinationFilter
                        genzoTrackApplyFilterSearchView={genzoTrackApplyFilterSearchView}
                        genzoTrackApplyFilterActionEvent={genzoTrackApplyFilterActionEvent}
                      />
                    </FilterCategoryAccordion>
                  )}

                  <FilterCategoryAccordion
                    title={translations.flight_filter_transit as string}
                    isFiltered={appliedFilters.includes(FlightFilterCategories.Transit)}
                  >
                    <StopoverAirportsFilter
                      genzoTrackApplyFilterActionEvent={genzoTrackApplyFilterActionEvent}
                      genzoTrackApplyFilterSearchView={genzoTrackApplyFilterSearchView}
                    />
                  </FilterCategoryAccordion>

                  {!isMultiCity && (
                    <FilterCategoryAccordion
                      title={translations.booking_sites as string}
                      isExpanded
                      isFiltered={appliedFilters.includes(FlightFilterCategories.BookingSites)}
                    >
                      <BookingSitesFilter
                        genzoTrackApplyFilterActionEvent={genzoTrackApplyFilterActionEvent}
                        genzoTrackApplyFilterSearchView={genzoTrackApplyFilterSearchView}
                      />
                    </FilterCategoryAccordion>
                  )}
                </div>
              </>
            ) : (
              <FlightFiltersSkeleton />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default FlightFilters;
